//@ts-check
import { DecodeWorkerPool } from "../workers/WorkerPool";
import { createWorker } from "../main/WorkerCreator";
import { HTTP2_PROXY_URL } from "./RcsConstants";
import { getRcsRemoteProxyUrl } from "./RcsEndpoints";
import { ensureRcsProxyConfig, resetRcsProxyConfig } from "./RcsIO";
import { RcsOpfsCacheStats } from "./RcsOpfsCacheStats.js";
import { drainPendingPromises } from "./RcsOpfsCache.js";

/**
 * Worker operation names accepted by {@link RcsWorkerPool.dispatchOpfsJob}.
 * @typedef {'RCS_OPFS_INIT'|'RCS_OPFS_STORE'|'RCS_OPFS_GET'|'RCS_OPFS_CLOSE'|'RCS_OPFS_CLEAR'|'RCS_OPFS_DELETE_BUCKET'} RcsOpfsOperation
 */

/**
 * @typedef {Object} RcsOpfsJobResponse
 * @property {Object} [result] - Payload returned by the fetch worker
 * @property {string} [result.error] - Present when the worker-side operation failed
 * @property {boolean} [result.hasCache] - INIT: cached params match current algorithm settings
 * @property {boolean} [result.success] - STORE: whether all blobs were persisted
 * @property {Array<Uint8Array|null>} [result.datas] - GET: one entry per requested hash
 */

/**
 * Upper bound for draining OPFS jobs before a close/teardown. Long enough that a slow but healthy
 * flush of pending stores still completes, short enough that a wedged worker does not keep the
 * loader alive indefinitely.
 */
const OPFS_DRAIN_TIMEOUT_MS = 10000;

/**
 * Manages the shared RCS fetch worker and its wiring to the DecodeWorkerPool.
 * Uses reference counting so the workers stay alive as long as at least one
 * RcsFileLoader is active, and are torn down when the last one unregisters.
 *
 * Stats counters (`inFlightRequests`, `inFlightRequestsSize`, `totalReceivedBytes`)
 * live here because the pool owns the fetch lifecycle.
 */
class RcsWorkerPool {

  constructor() {
    /** @type {any} */
    this._fetchWorker = null;
    /** @type {boolean} */
    this._initialized = false;
    /** @type {AbortController|null} Removes worker message listeners on teardown. */
    this._listenerAbort = null;
    /** @type {number} */
    this._refCount = 0;
    /**
     * Unified pending-response map for both fetch-decode and OPFS operations.
     * Keyed by the integer ID sent in the outgoing message (`requestId` for
     * fetch-decode, `jobId` for OPFS). A single counter ensures no collisions.
     * @type {Map<number, {resolve: (value: any) => void, reject: (reason: any) => void}>}
     */
    this._pendingRequests = new Map();
    /** @type {number} */
    this._nextRequestId = 0;
    /**
     * Promises for in-flight OPFS jobs. Allows callers to wait for all
     * pending OPFS operations (e.g. store jobs) before sending a close.
     * @type {Set<Promise<RcsOpfsJobResponse>>}
     */
    this._pendingOpfsPromises = new Set();
    /** @type {number} */
    this.totalReceivedBytes = 0;
    /** @type {number} */
    this.inFlightRequests = 0;
    /** @type {number} */
    this.inFlightRequestsSize = 0;
  }

  /**
   * Whether the worker pool has been initialized (fetch worker running).
   * @returns {boolean}
   */
  get isInitialized() {
    return this._initialized;
  }

  /**
   * Register a consumer (RcsFileLoader). Initializes workers on first call.
   */
  register() {
    this._refCount++;
    if (!this._initialized) this._initWorkers();
  }

  /**
   * Unregister a consumer. Tears down workers when the last consumer leaves.
   */
  unregister() {
    if (--this._refCount <= 0) this._teardown();
  }

  /**
   * Allocate a request ID and return a promise that resolves when the decode
   * worker posts the result.  Sends the fetch-and-decode operation to the
   * shared fetch worker.
   * @param {{length: number, [key: string]: any}} operationData - Payload forwarded to the fetch worker
   * @returns {Promise<any>}
   */
  dispatchFetchAndDecode(operationData) {
    if (!this._initialized) {
      throw new Error('RcsWorkerPool.dispatchFetchAndDecode() called before register()');
    }

    this.inFlightRequests++;
    this.inFlightRequestsSize += operationData.length;

    const promise = this._dispatch(
      { ...operationData, operation: 'RCS_FETCH_AND_DECODE' },
      'requestId'
    );

    return promise.finally(() => {
      this.inFlightRequests--;
      this.inFlightRequestsSize -= operationData.length;
    });
  }

  /**
   * Dispatch an OPFS operation to the fetch worker and return a Promise that
   * resolves with the worker's response payload.
   *
   * @param {RcsOpfsOperation} operation - One of the `RCS_OPFS_*` worker operations
   * @param {Object} data - Operation-specific payload merged with `{ jobId }`
   * @param {Transferable[]} [transferables] - Optional buffers to transfer to the worker
   * @returns {Promise<RcsOpfsJobResponse>}
   */
  dispatchOpfsJob(operation, data, transferables) {
    if (!this._initialized) {
      return Promise.reject(new Error('RCS workers not initialized'));
    }

    const promise = /** @type {Promise<RcsOpfsJobResponse>} */
    this._dispatch({ operation, ...data }, 'jobId', transferables);

    this._pendingOpfsPromises.add(promise);
    // then(cleanup, cleanup) rather than finally(): finally() returns a *new* promise that
    // adopts the rejection, and discarding it reports an unhandled rejection for every job
    // rejected by _teardown even though the caller handles the promise we return.
    const cleanup = () => {this._pendingOpfsPromises.delete(promise);};
    promise.then(cleanup, cleanup);
    return promise;
  }

  /**
   * Drains all currently in-flight OPFS jobs (store, get, init, etc.) by
   * waiting in a loop until no new jobs have been added during the await.
   * This guards against jobs that are dispatched while a previous batch is
   * settling (e.g. a STORE dispatched right before CLOSE).
   *
   * @param {number} [timeoutMs] - Give up after this long. The loop has no inherent bound: a
   * job that never settles (a wedged sync access handle, a worker that stopped replying) would
   * otherwise block model teardown for the rest of the page.
   * @returns {Promise<boolean>} True if the queue drained, false if the timeout was hit.
   */
  waitForPendingOpfsJobs(timeoutMs = OPFS_DRAIN_TIMEOUT_MS) {
    return drainPendingPromises(() => this._pendingOpfsPromises, timeoutMs);
  }

  // ---- private ----

  /**
   * Allocate an id, register the pending resolve/reject pair for it, and post the message
   * to the fetch worker. Fetch-decode results come back keyed by `requestId`, OPFS job
   * results by `jobId`; both share the id counter and the pending-request map.
   * @param {Object} message - Message payload, sent with the allocated id added under `idKey`
   * @param {'requestId'|'jobId'} idKey - Name of the id property in the message
   * @param {Transferable[]} [transferables] - Optional buffers to transfer to the worker
   * @returns {Promise<any>}
   */
  _dispatch(message, idKey, transferables) {
    const id = this._nextRequestId++;
    return new Promise((resolve, reject) => {
      this._pendingRequests.set(id, { resolve, reject });
      try {
        this._fetchWorker.doOperation({ ...message, [idKey]: id }, transferables || []);
      } catch (e) {
        // The worker never saw this id, so no reply will ever arrive to clear the entry.
        this._pendingRequests.delete(id);
        reject(e);
      }
    });
  }

  /**
   * Accumulates the OPFS cache stats reported once per fetch-and-decode request.
   * @param {{isLod0: boolean, cacheHit?: number, cacheMiss?: boolean, networkBytes?: number}} stats
   */
  _recordOpfsStats(stats) {
    if (stats.networkBytes !== undefined) {
      RcsOpfsCacheStats.networkBytesLoaded += stats.networkBytes;
    }
    if (stats.cacheHit !== undefined) {
      RcsOpfsCacheStats.cacheHits++;
      RcsOpfsCacheStats.cacheBytesLoaded += stats.cacheHit;
      if (stats.isLod0) {
        RcsOpfsCacheStats.lod0CacheHits++;
      }
    }
    if (stats.cacheMiss) {
      RcsOpfsCacheStats.cacheMisses++;
      if (stats.isLod0) {
        RcsOpfsCacheStats.lod0CacheMisses++;
      }
    }
  }

  _initWorkers() {
    if (this._initialized) return;
    this._initialized = true;

    // Listeners added below are tied to this signal so _teardown can remove
    // them all at once. This matters for the decode workers: they belong to
    // the shared DecodeWorkerPool and can outlive our teardown (e.g. while OTG
    // still holds a reference), so their listeners would otherwise accumulate
    // one set per register/teardown cycle.
    this._listenerAbort = new AbortController();
    const { signal } = this._listenerAbort;

    DecodeWorkerPool.register();

    const fetchWorker = createWorker('rcs-fetch');
    this._fetchWorker = fetchWorker;

    const decodeWorkers = /** @type {any[]} */DecodeWorkerPool.getWorkers();
    const portsForFetchWorker = [];

    for (const decodeWorker of decodeWorkers) {
      const channel = new MessageChannel();

      decodeWorker.doOperation(
        { operation: 'INSTALL_RCS_DECODE_PORT', port: channel.port1 },
        [channel.port1]
      );

      portsForFetchWorker.push(channel.port2);
    }

    fetchWorker.doOperation(
      { operation: 'INSTALL_DECODE_PORTS', ports: portsForFetchWorker },
      portsForFetchWorker
    );

    fetchWorker.addEventListener('message', (/** @type {MessageEvent} */event) => {
      const data = event.data;
      if (data.bytesReceived !== undefined) {
        this.totalReceivedBytes += data.bytesReceived;
        return;
      }
      if (data.jobId !== undefined) {
        const pending = this._pendingRequests.get(data.jobId);
        if (pending) {
          this._pendingRequests.delete(data.jobId);
          pending.resolve(data);
        }
        return;
      }
      if (data.rcsError) {
        const pending = this._pendingRequests.get(data.rcsError.requestId);
        if (pending) {
          this._pendingRequests.delete(data.rcsError.requestId);
          pending.reject(new Error(data.rcsError.message));
        }
      }
    }, { signal });

    // Worker-level `error` (uncaught exception in the worker) and `messageerror` (a
    // postMessage payload failed to deserialize) carry no per-request id, so we cannot
    // know which in-flight jobs were affected or whether the worker is still usable.
    // Rejecting every pending request is drastic — the worker may still complete some of
    // them, but those replies are dropped once the map is cleared — and preferable to
    // hanging forever: fetch-and-decode has no per-request timeout, and only init() caps
    // its own bootstrap wait. Callers that retry on the main thread resend with fresh ids
    // rather than depending on poisoned in-flight worker state.
    const failAllPending = (/** @type {string} */reason) => {
      for (const [, pending] of this._pendingRequests) {
        pending.reject(new Error(reason));
      }
      this._pendingRequests.clear();
      this._pendingOpfsPromises.clear();
    };
    fetchWorker.addEventListener('error', (/** @type {ErrorEvent} */event) => {
      failAllPending(`RCS fetch worker error: ${event.message ?? 'unknown'}`);
    }, { signal });
    fetchWorker.addEventListener('messageerror', () => {
      failAllPending('RCS fetch worker could not deserialize a message');
    }, { signal });

    for (const decodeWorker of decodeWorkers) {
      decodeWorker.addEventListener('message', (/** @type {MessageEvent} */event) => {
        const data = event.data;
        // The fetch worker attaches the cache stats for this request to its decode
        // request; the decode worker echoes them back with the result or the error.
        if (data.opfsStats) {
          this._recordOpfsStats(data.opfsStats);
        }
        if (data.rcsDecodeResult) {
          const pending = this._pendingRequests.get(data.requestId);
          if (pending) {
            this._pendingRequests.delete(data.requestId);
            pending.resolve(data.rcsDecodeResult);
          }
        } else if (data.rcsDecodeError) {
          const pending = this._pendingRequests.get(data.requestId);
          if (pending) {
            this._pendingRequests.delete(data.requestId);
            pending.reject(new Error(data.rcsDecodeError.message));
          }
        }
      }, { signal });
    }
  }

  /**
   * Establish proxy URLs for this page session and push them to the fetch worker on first
   * call. Call after {@link endpoint.getCdnUrl} becomes available (e.g. after
   * {@link initLoadContext}). Later loads may call this again with the same URLs; a CDN
   * change throws.
   *
   * We intentionally push the config here rather than baking it in when the worker is created:
   * the CDN URL is only guaranteed once the discovery endpoint has resolved, and if the first
   * RCS model is opened from a plain HTTP URL (no discovery round-trip) the endpoint would not
   * yet be populated. Setting it once at worker-creation time would then lock in an incorrect
   * (empty) endpoint with no way to correct it. Deferring to the first load after
   * initLoadContext avoids that boundary case; the ensureRcsProxyConfig guard still enforces
   * that the value never actually changes within a session.
   */
  syncProxyConfig() {
    if (!this._initialized) return;
    // Capture the remote URL once so the value recorded by ensureRcsProxyConfig and the value
    // pushed to the worker cannot diverge if the endpoint mutates between calls.
    const remoteProxyUrl = getRcsRemoteProxyUrl();
    const alreadyConfigured = ensureRcsProxyConfig(HTTP2_PROXY_URL, remoteProxyUrl);
    if (alreadyConfigured) return;
    this._fetchWorker.doOperation({
      operation: 'RCS_SET_PROXY',
      proxyUrl: HTTP2_PROXY_URL,
      remoteProxyUrl
    });
  }

  _teardown() {
    // Reset the ref count first so an over-call of unregister() won't result in a negative refCount.
    this._refCount = 0;

    if (!this._initialized) return;

    // Remove all message listeners registered in _initWorkers.
    this._listenerAbort?.abort();
    this._listenerAbort = null;

    if (this._fetchWorker) {
      this._fetchWorker.terminate();
      this._fetchWorker = null;
    }

    DecodeWorkerPool.unregister();

    for (const [, pending] of this._pendingRequests) {
      pending.reject(new Error('RCS workers torn down'));
    }
    this._pendingRequests.clear();
    // OPFS job rejections are handled via _pendingRequests above (same resolve/reject
    // pairs); this set only tracks Promise objects for waitForPendingOpfsJobs().
    this._pendingOpfsPromises.clear();

    this._initialized = false;

    // We have to reset the RCS proxy configuration to make
    // sure that a new proxy is correctly synced.
    resetRcsProxyConfig();
  }
}

export const rcsWorkerPool = new RcsWorkerPool();